import { useEffect, useState, RefObject } from 'react'
import get from 'lodash/fp/get'
import getOr from 'lodash/fp/getOr'
import map from 'lodash/fp/map'
import flow from 'lodash/fp/flow'
import snakeCase from 'lodash/fp/snakeCase'
import PasswordValidator from 'password-validator'
import { useMutation, useLazyQuery, useApolloClient } from '@apollo/client'
import { useLocation, useHistory } from 'react-router-dom'

import {
  TRACK_VIEW,
  GET_MY_EVENTS_IDS,
  CHECK_ROLE,
  GENERATE_UPLOAD_TOKEN,
} from '../graphql'
import {
  IParnershipTrack,
  IPasswordError,
  IuseReadToken,
  MyEvent,
  Role,
} from '../types'
import { useAppSelector } from '../hooks'

import { getRefIds } from './storageFunctions'
import { checkGuestRole, useCurrentUser } from './user'
import { uploadToGCS } from './imageUploadService'
import { errorAlert } from './general'
import { simpleAlert } from './alerts'

/**
 * @useOnScreen
 * This customHook function called useOnScreen takes a reference to an HTML element
 * and returns a boolean value indicating whether that element is in the visible screen or not.
 */
export const useOnScreen = (ref: RefObject<HTMLElement>): boolean => {
  // The isIntersecting state is used to track whether the element is on screen or not.
  const [isIntersecting, setIntersecting] = useState(false)
  useEffect(() => {
    // We create a new intersection observer.
    const observer = new IntersectionObserver(([entry]) =>
      setIntersecting(entry.isIntersecting)
    )

    // If the current reference (ref.current) is not null, we observe the referenced element.
    if (ref.current) {
      observer.observe(ref.current)
    }
  }, [ref])

  // We return the value of isIntersecting, which indicates if the element is on screen or not.
  return isIntersecting
}

/**
 * @useValidatePassword
 * This custom hook function called useValidatePassword takes a password string and
 * returns a list of errors indicating what is wrong with the password.
 */
export const useValidatePassword = (password: string): IPasswordError[] => {
  const [validate, setValidate] = useState<IPasswordError[]>([])

  useEffect(() => {
    const schema = new PasswordValidator()
    const passwordRules = schema
      .is()
      .min(8)
      .has()
      .uppercase(1)
      .has()
      .digits(1)
      .has()
      .symbols(1)

    setValidate(
      passwordRules.validate(password, { details: true }) as IPasswordError[]
    )
  }, [password])

  return validate
}

/**
 * @useIsMobile
 * This custom hook function called useIsMobile returns a boolean value
 * indicating whether the device is a mobile device or not
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(false)

  useEffect(() => {
    // ua = User Agent
    const ua = navigator.userAgent.toLowerCase()
    const isMobileDevice = ua.includes('mobile') || ua.includes('android')
    setIsMobile(isMobileDevice)
  }, [])

  return isMobile
}

/**
 * @useReadToken
 * This custom hook takes a jwtToken string and
 * returns the header, payload and signature of the token
 * BUT it doesn't validate the token signature; it is only used to read the token content
 */
export const useReadToken = (jwtToken: string): IuseReadToken => {
  let header: Record<string, unknown> | undefined
  let payload: Record<string, unknown> | undefined

  const parts = jwtToken.split('.')

  // jwt token must have 3 parts
  if (parts?.length === 3) {
    header = JSON.parse(atob(parts[0]))
    payload = JSON.parse(atob(parts[1]))
  }

  return {
    header,
    payload,
    signature: get('[2]', parts),
  }
}

/**
 * @useTrackView
 * This custom hook takes an eventId, guestId and source string and
 * returns a function that tracks the view
 */
interface ITrackView {
  eventId?: string
  guestId?: string
  source: string
  destination: string
}
export const useTrackView = (): {
  track: ({ eventId, guestId, source, destination }: ITrackView) => void
  loading: boolean
} => {
  const [trackView, { loading }] = useMutation(TRACK_VIEW)

  const track = ({ eventId, guestId, source, destination }: ITrackView) => {
    // we use sessionStorage to prevent multiple insert with each rerender
    // sessionStorage is cleared automatically when the tab is closed
    const sourceSC = snakeCase(source)
    if (sessionStorage.getItem(`track-${sourceSC}`) || loading) return
    sessionStorage.setItem(`track-${sourceSC}`, 'true')

    const variables = {
      guestId,
      eventId,
      source,
      destination,
    }
    trackView({ variables })
      .then(() => {
        // this is a background task, so we don't need to do anything here
        // if we need to do something in the future, we can add it here
      })
      .catch(() => {
        // same here but this is to prevent a crash
      })
  }

  return { track, loading }
}

/**
 * @usePartnership
 * This custom hook is to handle everything related to the partnerships
 */
export const usePartnership = (): {
  getPartnershipData: ({
    cta,
    source,
  }: {
    cta: string
    source: string
  }) => string | undefined
} => {
  const getPartnershipData = ({
    cta,
    source,
  }: {
    cta: string
    source: string
  }): string | undefined => {
    const refIds = getRefIds()
    // to return undefined instead of null that way apollo will not send the field "partnership" in the mutation
    if (!refIds || refIds.length <= 0) return undefined

    return flow(
      JSON.parse,
      map((refId: IParnershipTrack) => ({
        ...refId,
        cta,
        currentUrl: source,
      }))
    )(refIds)
  }

  return {
    getPartnershipData,
  }
}

/**
 * @useUpdateUrlParam
 * Custom hook to update a url param
 * @returns (key: string, value: string | number) => void
 *
 * @example
 * const updateUrlParam = useUpdateUrlParam()
 * updateUrlParam('key', 'value')
 * updateUrlParam('key', 1)
 */
export const useUpdateUrlParam = (): ((
  key: string,
  value: string | number
) => void) => {
  const history = useHistory()
  const location = useLocation()

  const updateUrlParam = (key: string, value: string | number): void => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set(key, value.toString())
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    })
  }

  return updateUrlParam
}

/**
 * @useCanManageEvent
 * Custom hook to check if the current user is an agent/admin or the user has access to the event
 * @returns boolean
 */
export const useCanManageEvent = (eventId?: string): boolean => {
  const { role } = useCurrentUser()
  const client = useApolloClient()
  const [canManage, setCanManage] = useState(false)

  useEffect(() => {
    // if the user is admin or concierge it's not necessary to check the events
    if (role === Role.Admin || role === Role.Concierge) {
      setCanManage(true)
      return
    }

    if (!eventId) {
      setCanManage(false)
      return
    }

    const checkCanManageEvent = async () => {
      const { data } = await client.query({ query: GET_MY_EVENTS_IDS })
      setCanManage(
        !!data?.me?.myEventList?.find((e: MyEvent) => e.id === eventId)
      )
    }

    checkCanManageEvent()
  }, [role, client, eventId])

  return canManage
}

/**
 * @useIsAgent
 * Custom hook to check if the current user is an Agent
 * @param type Role
 * @returns boolean
 */
export const useIsAgent = (type?: Role): boolean => {
  const { role } = useCurrentUser()

  if (type) return role === type
  return role === Role.Admin || role === Role.Concierge
}

export const useCanManagePosts = (): {
  canManage: boolean
  loading: boolean
} => {
  const isAdmin = useIsAgent(Role.Admin)
  const { currentEventId } = useAppSelector(state => state.site)
  const [checkRole, { loading }] = useLazyQuery(CHECK_ROLE)
  const [checkedEvent, setCheckedEvent] = useState<Record<string, boolean>>({})
  const [canManagePosts, setCanManagePosts] = useState<boolean>(() => isAdmin)

  useEffect(() => {
    if (loading || !currentEventId || isAdmin) return

    if (checkedEvent[currentEventId] !== undefined) {
      setCanManagePosts(checkedEvent[currentEventId])
      return
    }

    checkRole({ variables: { eventId: currentEventId } })
      .then(({ data }) => {
        const hasPermission = checkGuestRole({
          guestProfile: data?.checkRole,
          concierge: true,
          admin: true,
          host: true,
        })
        setCanManagePosts(hasPermission)
        setCheckedEvent(prev => ({ ...prev, [currentEventId]: hasPermission }))
      })
      .catch(() => {
        setCanManagePosts(false)
        setCheckedEvent(prev => ({ ...prev, [currentEventId]: false }))
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentEventId])

  return {
    canManage: canManagePosts,
    loading,
  }
}

/**
 * @useImageUpload
 * Custom hook to handle image upload with crop functionality
 * @param path - GCS path (e.g., 'avatars' or 'events/123')
 * @param name - File name (e.g., userId or 'main')
 * @param aspectRatio - Crop aspect ratio (default: 1)
 * @param onSuccess - Optional callback after successful upload
 * @returns Upload state and handlers
 */
interface UseImageUploadParams {
  path: string
  name: string
  aspectRatio?: number
  onSuccess?: (previewUrl: string) => void
}

interface UseImageUploadReturn {
  uploading: boolean
  showCropModal: boolean
  selectedImage: string | null
  imagePreview: string
  imageLoadError: boolean
  uploadImageMsg: string
  aspectRatio: number
  handleFileSelect: (event: { target: { files?: FileList | null } }) => void
  handleCropComplete: (file: File, previewUrl: string) => Promise<void>
  setShowCropModal: (show: boolean) => void
  setSelectedImage: (image: string | null) => void
  setImagePreview: (url: string) => void
  setImageLoadError: (error: boolean) => void
}

export const useImageUpload = ({
  path,
  name,
  aspectRatio = 1,
  onSuccess,
}: UseImageUploadParams): UseImageUploadReturn => {
  const [uploadImageMsg, setUploadImageMsg] = useState('')
  const [showCropModal, setShowCropModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [imageLoadError, setImageLoadError] = useState(false)

  const [generateUploadToken] = useMutation(GENERATE_UPLOAD_TOKEN)

  const handleFileSelect = (event: {
    target: { files?: FileList | null; value?: string }
  }) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedImage(reader.result as string)
        setShowCropModal(true)
      }
      reader.readAsDataURL(file)
      // Reset input to allow selecting the same file again
      if (event.target.value) {
        // eslint-disable-next-line no-param-reassign
        event.target.value = ''
      }
    }
  }

  const handleCropComplete = async (file: File, previewUrl: string) => {
    try {
      setUploading(true)
      setUploadImageMsg('Uploading image...')

      const { data: tokenData } = await generateUploadToken()

      const token = getOr('', 'generateUploadToken.result.token', tokenData)
      const tokenErrors = getOr([], 'generateUploadToken.errors', tokenData)

      if (tokenErrors.length > 0) {
        errorAlert(tokenErrors, 'Failed to generate upload token')
        return
      }

      const result = await uploadToGCS({
        file,
        path,
        ext: 'jpg',
        name,
        token,
      })

      if (result.success) {
        setImagePreview(previewUrl)
        setImageLoadError(false)
        onSuccess?.(previewUrl)
      } else {
        errorAlert(
          [{ key: 'upload', message: result.error || 'Upload failed' }],
          'Failed to upload image'
        )
      }
      setUploadImageMsg('')
    } catch (error) {
      simpleAlert({
        html: 'An unexpected error occurred during upload',
        icon: 'error',
      })
    } finally {
      setUploading(false)
    }
  }

  return {
    uploading,
    showCropModal,
    selectedImage,
    imagePreview,
    imageLoadError,
    uploadImageMsg,
    aspectRatio,
    handleFileSelect,
    handleCropComplete,
    setShowCropModal,
    setSelectedImage,
    setImagePreview,
    setImageLoadError,
  }
}
